import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { BuildingStorefrontIcon, PhotoIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import VendorLayout from '../../components/layout/VendorLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';

const VendorProfile = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('info');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);

  const { data: profile, isLoading } = useQuery(
    'vendor-profile',
    () => api.get('/vendors/profile').then(res => res.data.data)
  );

  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: profile
  });

  const updateProfileMutation = useMutation(
    (data) => {
      const formData = new FormData();
      
      // Add all text fields
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined && key !== 'logo') {
          formData.append(key, data[key]);
        }
      });
      
      // Add logo file if selected
      if (logoFile) {
        formData.append('logo', logoFile);
      }
      
      // Add banner file if selected
      if (bannerFile) {
        formData.append('banner', bannerFile);
      }
      
      return api.post('/vendors/profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('vendor-profile');
        toast.success('تم تحديث الملف الشخصي بنجاح');
        setLogoFile(null);
        setLogoPreview(null);
        setBannerFile(null);
        setBannerPreview(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'حدث خطأ');
      }
    }
  );

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB
        toast.error('حجم الصورة يجب أن يكون أقل من 2 ميجابايت');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('يجب اختيار صورة');
        return;
      }
      
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB
        toast.error('حجم الصورة يجب أن يكون أقل من 2 ميجابايت');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('يجب اختيار صورة');
        return;
      }
      
      setBannerFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = (data) => {
    updateProfileMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <VendorLayout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">الملف الشخصي</h1>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('info')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'info'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                معلومات المتجر
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'settings'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                الإعدادات
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'info' && (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Store Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اسم المتجر <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('store_name', { required: 'اسم المتجر مطلوب' })}
                    defaultValue={profile?.store_name}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                  {errors.store_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.store_name.message}</p>
                  )}
                </div>

                {/* Store Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    وصف المتجر
                  </label>
                  <textarea
                    {...register('description')}
                    defaultValue={profile?.description}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="اكتب وصفاً مختصراً عن متجرك..."
                  />
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      رقم الهاتف
                    </label>
                    <input
                      type="tel"
                      value={profile?.user?.phone || ''}
                      disabled
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      لتغيير رقم الهاتف، تواصل مع الإدارة
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      البريد الإلكتروني
                    </label>
                    <input
                      type="email"
                      value={profile?.user?.email || ''}
                      disabled
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      لتغيير البريد الإلكتروني، تواصل مع الإدارة
                    </p>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    العنوان <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('address', { required: 'العنوان مطلوب' })}
                    defaultValue={profile?.address}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                  {errors.address && (
                    <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
                  )}
                </div>

                {/* Logo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    شعار المتجر
                  </label>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      {logoPreview || profile?.logo ? (
                        <img
                          src={logoPreview || profile.logo}
                          alt="Store Logo"
                          className="w-24 h-24 rounded-lg object-cover border-2 border-gray-200"
                        />
                      ) : (
                        <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                          <PhotoIcon className="w-10 h-10 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                        id="logo-upload"
                      />
                      <label
                        htmlFor="logo-upload"
                        className="inline-block px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
                      >
                        اختر صورة
                      </label>
                      {logoFile && (
                        <button
                          type="button"
                          onClick={() => {
                            setLogoFile(null);
                            setLogoPreview(null);
                          }}
                          className="mr-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm font-medium text-red-700 hover:bg-red-100"
                        >
                          إلغاء
                        </button>
                      )}
                      <p className="mt-2 text-xs text-gray-500">
                        PNG, JPG, GIF حتى 2MB
                      </p>
                      {logoFile && (
                        <p className="mt-1 text-xs text-green-600">
                          تم اختيار: {logoFile.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Banner */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    خلفية المتجر (Banner)
                  </label>
                  <div className="space-y-4">
                    <div className="relative h-32 rounded-lg overflow-hidden border-2 border-gray-200">
                      {bannerPreview || profile?.banner ? (
                        <img
                          src={bannerPreview || profile.banner}
                          alt="Store Banner"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
                          <PhotoIcon className="w-12 h-12 text-white opacity-50" />
                        </div>
                      )}
                    </div>
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleBannerChange}
                        className="hidden"
                        id="banner-upload"
                      />
                      <label
                        htmlFor="banner-upload"
                        className="inline-block px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
                      >
                        اختر صورة خلفية
                      </label>
                      {bannerFile && (
                        <button
                          type="button"
                          onClick={() => {
                            setBannerFile(null);
                            setBannerPreview(null);
                          }}
                          className="mr-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm font-medium text-red-700 hover:bg-red-100"
                        >
                          إلغاء
                        </button>
                      )}
                      <p className="mt-2 text-xs text-gray-500">
                        PNG, JPG, GIF حتى 2MB - يفضل 1200x300 بكسل
                      </p>
                      {bannerFile && (
                        <p className="mt-1 text-xs text-green-600">
                          تم اختيار: {bannerFile.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-3">
                  <button
                    type="submit"
                    disabled={updateProfileMutation.isLoading}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {updateProfileMutation.isLoading ? 'جاري الحفظ...' : 'حفظ التغييرات'}
                  </button>
                </div>
              </form>
            )}

            {activeTab === 'settings' && (
              <div className="space-y-6">
                {/* Store Status */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">حالة المتجر</h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">حالة الموافقة</p>
                      <p className="text-sm text-gray-600">
                        {profile?.status === 'approved' ? 'تمت الموافقة' :
                         profile?.status === 'pending' ? 'قيد المراجعة' :
                         profile?.status === 'rejected' ? 'مرفوض' : 'غير معروف'}
                      </p>
                    </div>
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                      profile?.status === 'approved' ? 'bg-green-100 text-green-800' :
                      profile?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {profile?.status === 'approved' ? 'نشط' :
                       profile?.status === 'pending' ? 'معلق' : 'مرفوض'}
                    </span>
                  </div>
                </div>

                {/* Store Link */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">رابط المتجر</h3>
                  <div className="flex items-center gap-3">
                    <BuildingStorefrontIcon className="w-5 h-5 text-gray-400" />
                    <code className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm">
                      {window.location.origin}/store/{profile?.store_slug}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/store/${profile?.store_slug}`);
                        toast.success('تم نسخ الرابط');
                      }}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      نسخ
                    </button>
                  </div>
                </div>

                {/* Statistics */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">إحصائيات المتجر</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">التقييم</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {parseFloat(profile?.rating_average || 0).toFixed(1)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">عدد التقييمات</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {profile?.rating_count || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">تاريخ الانضمام</p>
                      <p className="text-sm font-medium text-gray-900">
                        {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('ar-SA') : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">آخر تحديث</p>
                      <p className="text-sm font-medium text-gray-900">
                        {profile?.updated_at ? new Date(profile.updated_at).toLocaleDateString('ar-SA') : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </VendorLayout>
  );
};

export default VendorProfile;
